/*
 * Revent: the content ids of a published conference, DERIVED from its edition
 * id by one pure function.
 *
 * WHY (2026-10-11). SF Tech Week's project folder, its body document and its
 * shard files live on constant ids written by hand into data/sf-2026.js, and
 * the publisher's table said how the next city would be added: "a row here
 * and its data/<city>-<year>.js". The first plan for LA Tech Week did exactly
 * that, and the owner stopped it: "wait why a built-in? it should be a
 * conference project, no? is there a blocker?". A conference is a project
 * folder and data; publishing one must need no code. The one thing the
 * constants were FOR still has to hold: dev, staging and production must
 * serve the same folder id to the same client, and the platform's sign-in
 * screen has to know that id before any database is asked. An id invented at
 * publish time would differ per environment, so the ids are a function of the
 * edition id and nothing else.
 *
 * THE SHAPE. `conferenceIds('la-2026')` answers
 *   folderId   <stem>-9000-000000000001   the project folder
 *   metaId     <stem>-9000-000000000002   its body, catalog.meta.json
 *   shardId(i) <stem>-9000-0000000001xx   shard i, xx its two hex digits
 * which is SF's own layout, with the stem (`xxxxxxxx-xxxx-4xxx`) being the
 * first fifteen hex digits of a 64-bit FNV-1a hash of
 * `revent:conference:<id>`, run twice. The `4` and the `9` are where a random content id
 * has its version and variant digits, so every validator that accepts a
 * content id accepts these; the fixed tail is what keeps them apart from any
 * id the platform mints at random.
 *
 * WHO READS IT. The publisher (scripts/revent-catalog.ts) writes on these
 * ids; the client (lib/editions.js) accepts a published conference only when
 * the ids it is told equal these, so a row in the function store can never
 * point a client at a folder of somebody else's; and the platform's sign-in
 * screen (lib/workspaces/flagship-apps.ts) keys a conference's screen by the
 * folder id. tests/rv-editions-published.test.mjs spells the ids for `la-2026` out
 * as literals: a change to this function is a change to where every
 * published conference lives, and must be seen as one.
 *
 * `sf-2026` IS NOT DERIVED. It was published on its hand-written constants
 * before this function existed and stays on them (data/sf-2026.js); the
 * callers above ask their own SF table first. This file knows no city.
 *
 * NO IMPORTS, on purpose: the platform and the publisher import this file as
 * it stands, with no bundle pin to follow. The hash is a literal copy of
 * tools/rv-ingest.js `fnv64` (a function file exports to tests and scripts,
 * never to the client), and the test holds the two equal.
 */

/* A calendar edition's id, `<city>-<year>` (the first arm of the edition
   grammar in lib/plan.js). A created conference's id IS its folder and has
   nothing to derive. Spelled with `[0-9]`, not `\d`: the narrow grammar the
   app had before a conference could be a folder is a literal a guard
   (tests/rv-guards.test.mjs) keeps out of every file, so that nobody
   validates an EDITION with it again. This is not that: it asks "is this
   one of the calendars", after the full grammar has already said it is an
   edition. Every module that asks imports it from here. */
export const PUBLISHED_ID_RE = /^[a-z]{2,4}-[0-9]{4}$/;
const MAX_SHARDS = 256;

/* 64-bit FNV-1a over UTF-16 code units, in four 16-bit limbs (no BigInt). */
function fnv64(str) {
  let h0 = 0x2325, h1 = 0x8422, h2 = 0x9ce4, h3 = 0xcbf2;
  for (let i = 0; i < str.length; i++) {
    h0 ^= str.charCodeAt(i);
    let t0 = h0 * 0x1b3, t1 = h1 * 0x1b3, t2 = h2 * 0x1b3 + (h0 << 8), t3 = h3 * 0x1b3 + (h1 << 8);
    t1 += t0 >>> 16; t2 += t1 >>> 16; t3 += t2 >>> 16;
    h0 = t0 & 0xffff; h1 = t1 & 0xffff; h2 = t2 & 0xffff; h3 = t3 & 0xffff;
  }
  const hex4 = (n) => n.toString(16).padStart(4, '0');
  return hex4(h3) + hex4(h2) + hex4(h1) + hex4(h0);
}

/* conferenceIds(editionId) -> { folderId, metaId, shardId(i) }, or null for
   anything that is not an ingested edition's id. `shardId(i)` answers null
   outside 0..255, as SF's own does. */
export function conferenceIds(editionId) {
  if (typeof editionId !== 'string' || !PUBLISHED_ID_RE.test(editionId)) return null;
  /* Two rounds: one FNV round barely moves the high digits when only the last
     character differs (`la-2026` and `la-2027` came out eleven digits alike),
     and the stem is read from the high end. The second round runs the first
     digest through sixteen more multiplications. */
  const seed = 'revent:conference:' + editionId;
  const h = fnv64(fnv64(seed) + ':' + seed);
  const stem = h.slice(0, 8) + '-' + h.slice(8, 12) + '-4' + h.slice(12, 15) + '-9000-';
  return {
    folderId: stem + '000000000001',
    metaId: stem + '000000000002',
    shardId(i) {
      const n = Number(i);
      if (!Number.isInteger(n) || n < 0 || n >= MAX_SHARDS) return null;
      return stem + '0000000001' + n.toString(16).padStart(2, '0');
    },
  };
}

/* conferenceFolderId(editionId) -> the folder id alone, or null: what a
   caller that only keys on the folder asks. */
export function conferenceFolderId(editionId) {
  const ids = conferenceIds(editionId);
  return ids ? ids.folderId : null;
}
